import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/layouts/Layout';
import { Login } from '@/pages/Login';
import { Dashboard } from '@/pages/Dashboard';
import { Content } from '@/pages/Content';
import { PathwaysList, PathwayForm } from '@/pages/pathways';
import { TracksList, TrackForm } from '@/pages/tracks';
import { LessonsList, LessonForm } from '@/pages/lessons';
import { AchievementsList, AchievementForm } from '@/pages/achievements';
import {
  ArticleSourcesList,
  ArticleSourceForm,
  SyncsList,
  SyncForm,
  SyncPreviewPage,
  SyncJobsList,
  SyncJobDetail,
} from '@/pages/syncs';
import { Forbidden } from '@/pages/Forbidden';

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { status, isAdmin, firebaseUser, firebaseReady } = useAuth();
  if (!firebaseReady || status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!firebaseUser) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/forbidden" replace />;
  return <>{children}</>;
}

function RequireGuest({ children }: { children: React.ReactNode }) {
  const { status, isAdmin, firebaseUser, firebaseReady } = useAuth();
  if (!firebaseReady || status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading…</div>
      </div>
    );
  }
  if (firebaseUser && isAdmin) return <Navigate to="/" replace />;
  if (firebaseUser && !isAdmin) return <Navigate to="/forbidden" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<RequireGuest><Login /></RequireGuest>} />
      <Route path="/forbidden" element={<Forbidden />} />
      <Route
        path="/"
        element={
          <RequireAdmin>
            <Layout />
          </RequireAdmin>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="content" element={<Content />} />
        <Route path="pathways" element={<PathwaysList />} />
        <Route path="pathways/new" element={<PathwayForm />} />
        <Route path="pathways/:id/edit" element={<PathwayForm />} />
        <Route path="tracks" element={<TracksList />} />
        <Route path="tracks/new" element={<TrackForm />} />
        <Route path="tracks/:id/edit" element={<TrackForm />} />
        <Route path="lessons" element={<LessonsList />} />
        <Route path="lessons/new" element={<LessonForm />} />
        <Route path="lessons/:id/edit" element={<LessonForm />} />
        <Route path="achievements" element={<AchievementsList />} />
        <Route path="achievements/new" element={<AchievementForm />} />
        <Route path="achievements/:id/edit" element={<AchievementForm />} />
        <Route path="syncs/jobs" element={<SyncJobsList />} />
        <Route path="syncs/jobs/:id" element={<SyncJobDetail />} />
        <Route path="syncs/sources" element={<ArticleSourcesList />} />
        <Route path="syncs/sources/new" element={<ArticleSourceForm />} />
        <Route path="syncs/sources/:id/edit" element={<ArticleSourceForm />} />
        <Route path="syncs/new" element={<SyncForm />} />
        <Route path="syncs/:id/edit" element={<SyncForm />} />
        <Route path="syncs/:id/preview" element={<SyncPreviewPage />} />
        <Route path="syncs" element={<SyncsList />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
